import React, { useState, useEffect, useMemo } from "react";
import { useAuth } from "@/App";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { toast } from "sonner";
import {
  Loader2,
  Download,
  RefreshCw,
  Database,
  ArrowLeft,
  ArrowDown,
  ArrowUp,
  TrendingUp,
  TrendingDown,
  ExternalLink,
  X,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SortHeader = ({ label, sortKey, current, dir, onChange, align = "left" }) => (
  <th
    className={`p-2 text-${align} cursor-pointer select-none hover:bg-teal-100`}
    onClick={() => {
      const nextDir = current === sortKey && dir === "desc" ? "asc" : "desc";
      onChange(sortKey, nextDir);
    }}
    data-testid={`cb-sort-${sortKey}`}
  >
    <span className="inline-flex items-center gap-1">
      {label}
      {current === sortKey ? (dir === "desc" ? <ArrowDown className="w-3 h-3" /> : <ArrowUp className="w-3 h-3" />) : null}
    </span>
  </th>
);

const ClientBalancePage = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [clientFilter, setClientFilter] = useState("");
  const [direction, setDirection] = useState("all"); // all | cr | dr | zero
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState("balance");
  const [sortDir, setSortDir] = useState("desc");
  const [clientOptions, setClientOptions] = useState([]);
  // Drill-down state
  const [drillOpen, setDrillOpen] = useState(false);
  const [drillClient, setDrillClient] = useState("");
  const [drillLoading, setDrillLoading] = useState(false);
  const [drillData, setDrillData] = useState(null);
  const [drillView, setDrillView] = useState("both"); // both | raw | cost

  const openDrill = async (code) => {
    setDrillClient(code);
    setDrillOpen(true);
    setDrillLoading(true);
    setDrillData(null);
    try {
      const p = new URLSearchParams();
      p.append("client_code", code);
      if (dateFrom) p.append("date_from", dateFrom);
      if (dateTo) p.append("date_to", dateTo);
      const r = await fetch(`${BACKEND_URL}/api/payment-verification/raw-funds/client-balance/detail?${p}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      if (!r.ok) { toast.error(d.detail || "Failed to load detail"); return; }
      setDrillData(d);
    } catch (e) {
      toast.error("Network error: " + e.message);
    } finally {
      setDrillLoading(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (dateFrom) p.append("date_from", dateFrom);
      if (dateTo) p.append("date_to", dateTo);
      if (clientFilter) p.append("client_code", clientFilter);
      p.append("sort_key", sortKey);
      p.append("sort_dir", sortDir);
      const r = await fetch(`${BACKEND_URL}/api/payment-verification/raw-funds/client-balance?${p}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      if (!r.ok) { toast.error(d.detail || "Failed to load"); return; }
      setData(d);
      if (clientOptions.length === 0) {
        setClientOptions(d.rows.map((x) => x.client_code).sort());
      }
    } catch (e) {
      toast.error("Network error: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(); }, [sortKey, sortDir]);

  const filteredRows = useMemo(() => {
    if (!data) return [];
    let rows = data.rows;
    if (direction !== "all") {
      rows = rows.filter((r) => {
        if (direction === "cr") return r.direction === "Cr";
        if (direction === "dr") return r.direction === "Dr";
        return r.direction === "—";
      });
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter((r) => r.client_code.toLowerCase().includes(q));
    }
    return rows;
  }, [data, direction, search]);

  // Totals after the client-side filters.
  const totals = useMemo(() => {
    return filteredRows.reduce(
      (acc, r) => {
        acc.raw += r.raw_inr;
        acc.cost += r.costing_inr;
        acc.bal += r.balance;
        return acc;
      },
      { raw: 0, cost: 0, bal: 0 },
    );
  }, [filteredRows]);

  const handleSort = (k, d) => { setSortKey(k); setSortDir(d); };

  const exportExcel = () => {
    const p = new URLSearchParams();
    if (dateFrom) p.append("date_from", dateFrom);
    if (dateTo) p.append("date_to", dateTo);
    if (clientFilter) p.append("client_code", clientFilter);
    p.append("sort_key", sortKey);
    p.append("sort_dir", sortDir);
    fetch(`${BACKEND_URL}/api/payment-verification/raw-funds/client-balance/export?${p}`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(async (r) => {
      if (!r.ok) { toast.error("Export failed"); return; }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const cd = r.headers.get("content-disposition") || "";
      const m = cd.match(/filename="([^"]+)"/);
      a.download = m ? m[1] : "client_balance.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Excel downloaded");
    }).catch((e) => toast.error("Export failed: " + e.message));
  };

  const setQuickRange = (kind) => {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();
    let f = "", t = "";
    if (kind === "this_month") {
      f = `${y}-${String(m + 1).padStart(2, "0")}-01`;
      const last = new Date(y, m + 1, 0).getDate();
      t = `${y}-${String(m + 1).padStart(2, "0")}-${String(last).padStart(2, "0")}`;
    } else if (kind === "last_month") {
      const ym = m === 0 ? y - 1 : y;
      const mm = m === 0 ? 12 : m;
      f = `${ym}-${String(mm).padStart(2, "0")}-01`;
      const last = new Date(ym, mm, 0).getDate();
      t = `${ym}-${String(mm).padStart(2, "0")}-${String(last).padStart(2, "0")}`;
    } else if (kind === "fy") {
      // Indian FY (Apr–Mar)
      const fyStart = m >= 3 ? y : y - 1;
      f = `${fyStart}-04-01`;
      t = `${fyStart + 1}-03-31`;
    } else {
      f = ""; t = "";
    }
    setDateFrom(f);
    setDateTo(t);
  };

  const fmt = (n) => n.toLocaleString("en-IN", { maximumFractionDigits: 2 });

  return (
    <div className="space-y-4" data-testid="client-balance-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Database className="w-6 h-6 text-teal-600" />
            Client Balance
          </h1>
          <p className="text-xs text-slate-500">
            Raw Payment Collection (per client) − Shipment Costing (per client) = Cr / Dr balance.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/payment-verification?tab=raw-funds" data-testid="cb-back">
            <Button variant="outline" size="sm">
              <ArrowLeft className="w-3.5 h-3.5 mr-1" />
              Back to Raw Data
            </Button>
          </Link>
          <Link to="/payment-verification?tab=raw-funds" data-testid="cb-raw-link">
            <Button variant="outline" size="sm" className="border-indigo-300 text-indigo-700 hover:bg-indigo-50">
              <Database className="w-3.5 h-3.5 mr-1" />
              Raw Payments
            </Button>
          </Link>
          <Button size="sm" onClick={exportExcel} disabled={loading || !data || data.rows.length === 0} className="bg-emerald-600 hover:bg-emerald-700" data-testid="cb-export">
            <Download className="w-3.5 h-3.5 mr-1" />
            Export Excel
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="text-[11px] text-slate-500 block">From</label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 w-40" data-testid="cb-from" />
            </div>
            <div>
              <label className="text-[11px] text-slate-500 block">To</label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 w-40" data-testid="cb-to" />
            </div>
            <div className="flex flex-col gap-0.5">
              <label className="text-[11px] text-slate-500">Quick</label>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" className="h-9 text-xs" onClick={() => setQuickRange("this_month")} data-testid="cb-quick-this-month">This Month</Button>
                <Button size="sm" variant="outline" className="h-9 text-xs" onClick={() => setQuickRange("last_month")} data-testid="cb-quick-last-month">Last Month</Button>
                <Button size="sm" variant="outline" className="h-9 text-xs" onClick={() => setQuickRange("fy")} data-testid="cb-quick-fy">FY</Button>
                <Button size="sm" variant="ghost" className="h-9 text-xs" onClick={() => setQuickRange("all")}>All</Button>
              </div>
            </div>
            <div>
              <label className="text-[11px] text-slate-500 block">Client (one)</label>
              <Select value={clientFilter || "__all__"} onValueChange={(v) => setClientFilter(v === "__all__" ? "" : v)}>
                <SelectTrigger className="h-9 w-44" data-testid="cb-client-select"><SelectValue placeholder="All clients" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All clients</SelectItem>
                  {clientOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[11px] text-slate-500 block">Cr/Dr</label>
              <Select value={direction} onValueChange={setDirection}>
                <SelectTrigger className="h-9 w-32" data-testid="cb-direction-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="cr">Credit only</SelectItem>
                  <SelectItem value="dr">Debit only</SelectItem>
                  <SelectItem value="zero">Zero only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 min-w-[160px]">
              <label className="text-[11px] text-slate-500 block">Search</label>
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="client code…" className="h-9" data-testid="cb-search" />
            </div>
            <Button size="sm" onClick={fetchData} disabled={loading} data-testid="cb-apply">
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><RefreshCw className="w-3.5 h-3.5 mr-1" />Apply</>}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => { setDateFrom(""); setDateTo(""); setClientFilter(""); setDirection("all"); setSearch(""); setTimeout(fetchData, 0); }}>Clear</Button>
          </div>
        </CardContent>
      </Card>

      {/* KPI strip */}
      {data && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Card>
            <CardContent className="p-4">
              <div className="text-[11px] uppercase text-slate-500">Clients</div>
              <div className="text-2xl font-bold" data-testid="cb-kpi-clients">{filteredRows.length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-[11px] uppercase text-slate-500">Raw Payments</div>
              <div className="text-2xl font-bold text-emerald-700" data-testid="cb-kpi-raw">₹{fmt(totals.raw)}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-[11px] uppercase text-slate-500">Costing</div>
              <div className="text-2xl font-bold text-amber-700" data-testid="cb-kpi-cost">₹{fmt(totals.cost)}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-[11px] uppercase text-slate-500">Net Balance</div>
              <div className={`text-2xl font-bold flex items-center gap-1 ${totals.bal > 0 ? "text-emerald-700" : totals.bal < 0 ? "text-rose-700" : "text-slate-700"}`} data-testid="cb-kpi-balance">
                {totals.bal > 0 ? <TrendingUp className="w-5 h-5" /> : totals.bal < 0 ? <TrendingDown className="w-5 h-5" /> : null}
                ₹{fmt(Math.abs(totals.bal))} {totals.bal !== 0 && <span className="text-xs ml-1">({totals.bal > 0 ? "Cr" : "Dr"})</span>}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Table */}
      <Card>
        <CardHeader className="py-3">
          <CardTitle className="text-base">Per-Client Breakdown ({filteredRows.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-teal-50">
              <tr>
                <SortHeader label="Client" sortKey="client_code" current={sortKey} dir={sortDir} onChange={handleSort} />
                <SortHeader label="Raw Rows" sortKey="raw_rows" current={sortKey} dir={sortDir} onChange={handleSort} align="right" />
                <SortHeader label="Raw INR" sortKey="raw_inr" current={sortKey} dir={sortDir} onChange={handleSort} align="right" />
                <SortHeader label="Cost Orders" sortKey="costing_orders" current={sortKey} dir={sortDir} onChange={handleSort} align="right" />
                <SortHeader label="Costing INR" sortKey="costing_inr" current={sortKey} dir={sortDir} onChange={handleSort} align="right" />
                <SortHeader label="Balance" sortKey="balance" current={sortKey} dir={sortDir} onChange={handleSort} align="right" />
                <th className="p-2 text-center">Cr/Dr</th>
                <SortHeader label="First Raw Date" sortKey="raw_first_date" current={sortKey} dir={sortDir} onChange={handleSort} />
                <SortHeader label="Last Raw Date" sortKey="raw_last_date" current={sortKey} dir={sortDir} onChange={handleSort} />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="text-center text-slate-500 p-8"><Loader2 className="w-5 h-5 inline animate-spin mr-2" />Computing…</td></tr>
              ) : filteredRows.length === 0 ? (
                <tr><td colSpan={9} className="text-center text-slate-500 p-8 italic">No clients in this slice.</td></tr>
              ) : filteredRows.map((r) => (
                <tr
                  key={r.client_code}
                  className="border-t border-slate-100 hover:bg-teal-50 cursor-pointer"
                  onClick={() => openDrill(r.client_code)}
                  data-testid={`cb-row-${r.client_code}`}
                  title="Click for drill-down"
                >
                  <td className="p-2 font-mono font-semibold">{r.client_code}</td>
                  <td className="p-2 text-right">{r.raw_rows.toLocaleString()}</td>
                  <td className="p-2 text-right font-mono text-emerald-700">{r.raw_inr ? `₹${fmt(r.raw_inr)}` : "—"}</td>
                  <td className="p-2 text-right">{r.costing_orders.toLocaleString()}</td>
                  <td className="p-2 text-right font-mono text-amber-700">{r.costing_inr ? `₹${fmt(r.costing_inr)}` : "—"}</td>
                  <td className={`p-2 text-right font-mono font-bold ${r.direction === "Cr" ? "text-emerald-700" : r.direction === "Dr" ? "text-rose-700" : "text-slate-500"}`}>
                    {r.balance >= 0 ? `+₹${fmt(r.balance)}` : `-₹${fmt(Math.abs(r.balance))}`}
                  </td>
                  <td className="p-2 text-center">
                    <Badge className={r.direction === "Cr" ? "bg-emerald-600 text-white" : r.direction === "Dr" ? "bg-rose-600 text-white" : "bg-slate-300 text-slate-700"}>{r.direction}</Badge>
                  </td>
                  <td className="p-2 font-mono text-slate-600">{r.raw_first_date || "—"}</td>
                  <td className="p-2 font-mono text-slate-600">{r.raw_last_date || "—"}</td>
                </tr>
              ))}
            </tbody>
            {filteredRows.length > 0 && (
              <tfoot className="bg-slate-100 border-t-2 border-slate-300">
                <tr>
                  <td className="p-2 font-bold">Total ({filteredRows.length})</td>
                  <td></td>
                  <td className="p-2 text-right font-bold text-emerald-700">₹{fmt(totals.raw)}</td>
                  <td></td>
                  <td className="p-2 text-right font-bold text-amber-700">₹{fmt(totals.cost)}</td>
                  <td className={`p-2 text-right font-bold ${totals.bal > 0 ? "text-emerald-700" : totals.bal < 0 ? "text-rose-700" : "text-slate-700"}`}>
                    {totals.bal >= 0 ? `+₹${fmt(totals.bal)}` : `-₹${fmt(Math.abs(totals.bal))}`}
                  </td>
                  <td className="p-2 text-center">
                    <Badge className={totals.bal > 0 ? "bg-emerald-600 text-white" : totals.bal < 0 ? "bg-rose-600 text-white" : "bg-slate-300 text-slate-700"}>
                      {totals.bal > 0 ? "Cr" : totals.bal < 0 ? "Dr" : "—"}
                    </Badge>
                  </td>
                  <td colSpan={2}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </CardContent>
      </Card>

      {/* Per-client drill-down side panel */}
      <Sheet open={drillOpen} onOpenChange={setDrillOpen}>
        <SheetContent side="right" className="w-full sm:max-w-5xl overflow-y-auto" data-testid="cb-drill-sheet">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Database className="w-5 h-5 text-teal-600" />
              {drillClient} — Drill-down
              {drillData && drillData.totals && (
                <Badge className={`ml-2 ${drillData.totals.direction === "Cr" ? "bg-emerald-600" : drillData.totals.direction === "Dr" ? "bg-rose-600" : "bg-slate-400"} text-white`}>
                  Balance ₹{Math.abs(drillData.totals.balance).toLocaleString("en-IN", { maximumFractionDigits: 2 })} {drillData.totals.direction}
                </Badge>
              )}
            </SheetTitle>
            <SheetDescription>
              Every raw payment row + every shipment costing order that contributed to this client's balance{(dateFrom || dateTo) ? ` between ${dateFrom || "start"} and ${dateTo || "today"}` : ""}.
            </SheetDescription>
          </SheetHeader>

          {drillLoading ? (
            <div className="py-10 text-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Loading…</div>
          ) : !drillData ? (
            <div className="py-10 text-center text-slate-500 italic">No data</div>
          ) : (
            <div className="space-y-4 mt-3">
              {/* Totals */}
              <div className="grid grid-cols-3 gap-2">
                <div className="bg-emerald-50 border border-emerald-200 rounded p-3">
                  <div className="text-[10px] uppercase text-emerald-700">Raw Payments</div>
                  <div className="text-lg font-bold text-emerald-700">₹{fmt(drillData.totals.raw_inr)}</div>
                  <div className="text-[10px] text-slate-500">{drillData.totals.raw_count} row(s)</div>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded p-3">
                  <div className="text-[10px] uppercase text-amber-700">Costing</div>
                  <div className="text-lg font-bold text-amber-700">₹{fmt(drillData.totals.costing_inr)}</div>
                  <div className="text-[10px] text-slate-500">{drillData.totals.costing_count} order(s)</div>
                </div>
                <div className={`${drillData.totals.direction === "Cr" ? "bg-emerald-50 border-emerald-200" : drillData.totals.direction === "Dr" ? "bg-rose-50 border-rose-200" : "bg-slate-50 border-slate-200"} border rounded p-3`}>
                  <div className="text-[10px] uppercase text-slate-600">Balance</div>
                  <div className={`text-lg font-bold ${drillData.totals.direction === "Cr" ? "text-emerald-700" : drillData.totals.direction === "Dr" ? "text-rose-700" : "text-slate-700"}`}>
                    {drillData.totals.balance >= 0 ? `+₹${fmt(drillData.totals.balance)}` : `-₹${fmt(Math.abs(drillData.totals.balance))}`}
                  </div>
                  <div className="text-[10px] text-slate-500">{drillData.totals.direction}</div>
                </div>
              </div>

              {/* View toggle */}
              <div className="flex items-center gap-1 text-xs">
                <span className="text-slate-500 mr-1">View:</span>
                {[
                  { v: "both", l: "Both" },
                  { v: "raw", l: `Raw (${drillData.raw_rows.length})` },
                  { v: "cost", l: `Costing (${drillData.costing_orders.length})` },
                ].map((b) => (
                  <button
                    key={b.v}
                    onClick={() => setDrillView(b.v)}
                    className={`px-2 py-1 rounded ${drillView === b.v ? "bg-teal-600 text-white" : "bg-slate-100 hover:bg-slate-200"}`}
                    data-testid={`cb-drill-view-${b.v}`}
                  >
                    {b.l}
                  </button>
                ))}
                {drillData.code_variants.raw.length > 1 || drillData.code_variants.shipment_orders.length > 1 ? (
                  <span className="ml-auto text-[10px] text-slate-500 italic" title={`Variants merged · raw: ${drillData.code_variants.raw.join(", ")} · costing: ${drillData.code_variants.shipment_orders.join(", ")}`}>
                    Merged variants
                  </span>
                ) : null}
              </div>

              <div className={`grid gap-3 ${drillView === "both" ? "grid-cols-2" : "grid-cols-1"}`}>
                {(drillView === "both" || drillView === "raw") && (
                  <div>
                    <div className="text-xs font-semibold text-emerald-700 mb-1">Raw Payments ({drillData.raw_rows.length})</div>
                    <div className="border border-emerald-200 rounded max-h-[60vh] overflow-y-auto">
                      <table className="w-full text-[11px]">
                        <thead className="bg-emerald-50 sticky top-0">
                          <tr>
                            <th className="p-1.5 text-left">Date</th>
                            <th className="p-1.5 text-left">Source</th>
                            <th className="p-1.5 text-left">Narration</th>
                            <th className="p-1.5 text-right">INR</th>
                          </tr>
                        </thead>
                        <tbody>
                          {drillData.raw_rows.length === 0 ? (
                            <tr><td colSpan={4} className="p-3 text-center text-slate-400 italic">No raw rows</td></tr>
                          ) : drillData.raw_rows.map((r, i) => (
                            <tr key={`raw-${i}`} className="border-t border-emerald-100">
                              <td className="p-1.5 font-mono">{r.date_ddmmyy}</td>
                              <td className="p-1.5 font-mono">{r.source}</td>
                              <td className="p-1.5 truncate max-w-[180px]" title={r.narration}>{r.narration}</td>
                              <td className="p-1.5 text-right font-mono text-emerald-700">₹{fmt(r.inward_inr)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                {(drillView === "both" || drillView === "cost") && (
                  <div>
                    <div className="text-xs font-semibold text-amber-700 mb-1">Costing Orders ({drillData.costing_orders.length})</div>
                    <div className="border border-amber-200 rounded max-h-[60vh] overflow-y-auto">
                      <table className="w-full text-[11px]">
                        <thead className="bg-amber-50 sticky top-0">
                          <tr>
                            <th className="p-1.5 text-left">Date</th>
                            <th className="p-1.5 text-left">Tracking</th>
                            <th className="p-1.5 text-left">Product</th>
                            <th className="p-1.5 text-right">Qty</th>
                            <th className="p-1.5 text-right">Cost</th>
                          </tr>
                        </thead>
                        <tbody>
                          {drillData.costing_orders.length === 0 ? (
                            <tr><td colSpan={5} className="p-3 text-center text-slate-400 italic">No costing orders</td></tr>
                          ) : drillData.costing_orders.map((c, i) => (
                            <tr key={`cost-${i}`} className="border-t border-amber-100">
                              <td className="p-1.5 font-mono">{c.order_date}</td>
                              <td className="p-1.5 font-mono">{c.tracking}</td>
                              <td className="p-1.5 truncate max-w-[160px]" title={c.product_name}>{c.product_name}</td>
                              <td className="p-1.5 text-right">{c.quantity}</td>
                              <td className="p-1.5 text-right font-mono text-amber-700">₹{fmt(c.total_cost)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default ClientBalancePage;
